import { useEffect, useCallback, useRef } from 'react';

export const useAutoLoad = ({
  vehicle,
  startDate,
  endDate,
  currentVehicle,
  setCurrentVehicle,
  currentDateRange,
  setCurrentDateRange,
  mapInitialized,
  fetchTrackData,
  initializeMap,
  mapContainerRef,
  ensureValidDate,
  saveVehicleToLocalStorage,
  saveDateRangeToLocalStorage,
  formatDate,
  isLoading
}) => {

  // Рефы для предотвращения повторных вызовов
  const lastVehicleId = useRef(null);
  const lastDateRange = useRef(null);
  const isInitialized = useRef(false);
  const fetchTimeout = useRef(null);
  const autoLoadCompleted = useRef(false);

  // Функция проверки localStorage при инициализации (точная копия из TrackMap)
  const checkLocalStorage = useCallback(() => {
    if (isInitialized.current) return; // Предотвращаем повторную инициализацию
    
    try {
      console.log('TrackReport: Проверка localStorage при инициализации');
      
      // Проверяем сохраненный транспорт (выполняем первым для приоритета)
      const savedVehicle = localStorage.getItem('lastSelectedVehicle');
      if (savedVehicle) {
        const parsedVehicle = JSON.parse(savedVehicle);
        if (parsedVehicle && parsedVehicle.id) {
          console.log('TrackReport: Прочитаны данные транспорта из localStorage:', parsedVehicle);
          
          // Если текущее транспортное средство из props имеет приоритет
          if (vehicle && vehicle.id) {
            console.log('TrackReport: Используется транспорт из props вместо localStorage:', vehicle);
            lastVehicleId.current = vehicle.id;
          } else {
            // Нормализуем данные, если timestamp передан как число
            if (parsedVehicle.timestamp && typeof parsedVehicle.timestamp === 'number') {
              parsedVehicle.timestampDate = new Date(parsedVehicle.timestamp);
            }
            
            // Добавляем источник данных
            parsedVehicle._source = 'localStorage_initial';
            parsedVehicle._updateTimestamp = new Date().getTime();
            
            setCurrentVehicle(parsedVehicle);
            lastVehicleId.current = parsedVehicle.id;
          }
        }
      }
      
      // Проверяем сохраненный диапазон дат
      const savedRange = localStorage.getItem('lastDateRange');
      if (savedRange) {
        const parsedRange = JSON.parse(savedRange);
        if (parsedRange.startDate && parsedRange.endDate) {
          console.log('TrackReport: Прочитан диапазон дат из localStorage:', parsedRange);
          
          // Используем функцию для обеспечения валидных дат
          const newRange = {
            startDate: ensureValidDate(parsedRange.startDate),
            endDate: ensureValidDate(parsedRange.endDate)
          };
          
          if (newRange.startDate && newRange.endDate) {
            setCurrentDateRange(newRange);
            lastDateRange.current = `${newRange.startDate.getTime()}-${newRange.endDate.getTime()}`;
          }
        }
      }
      
      isInitialized.current = true;
    } catch (error) {
      console.warn('TrackReport: Ошибка при чтении данных из localStorage:', error);
    }
  }, [vehicle, ensureValidDate, setCurrentVehicle, setCurrentDateRange]);

  // Безопасная функция вызова fetchTrackData с защитой от повторов
  const safeFetchTrackData = useCallback((vehicleData, dateRange, source = 'unknown') => {
    // Проверяем, что не загружаем уже
    if (isLoading) {
      console.log('TrackReport: Пропуск загрузки - уже идет загрузка');
      return;
    }

    // Проверяем валидность данных
    if (!vehicleData || !vehicleData.id || !dateRange || !dateRange.startDate || !dateRange.endDate) {
      console.log('TrackReport: Пропуск загрузки - невалидные данные:', { vehicleData, dateRange });
      return;
    }

    // Проверяем, не повторяется ли запрос (но только если есть реальные данные трека)
    const vehicleId = vehicleData.id;
    const dateKey = `${dateRange.startDate.getTime()}-${dateRange.endDate.getTime()}`;
    
    if (lastVehicleId.current === vehicleId && lastDateRange.current === dateKey) {
      console.log('TrackReport: Данные уже помечены как загруженные, но проверяем наличие trackData...');
      // Дополнительная проверка - есть ли реальные данные трека
      // Эта проверка должна быть передана извне, но пока пропускаем эту защиту для автозагрузки
      if (source !== 'map_initialized') {
        console.log('TrackReport: Пропуск загрузки - те же данные уже загружены');
        return;
      } else {
        console.log('TrackReport: Принудительная загрузка при инициализации карты');
      }
    }

    console.log(`TrackReport: Безопасная загрузка данных из источника: ${source}`, {
      vehicle: vehicleData.name,
      startDate: dateRange.startDate.toISOString(),
      endDate: dateRange.endDate.toISOString()
    });

    // Сохраняем текущие параметры
    lastVehicleId.current = vehicleId;
    lastDateRange.current = dateKey;

    // Очищаем предыдущий таймаут
    if (fetchTimeout.current) {
      clearTimeout(fetchTimeout.current);
    }

    // Вызываем загрузку с небольшой задержкой
    fetchTimeout.current = setTimeout(() => {
      fetchTrackData(vehicleData, dateRange);
    }, 300);

  }, [isLoading, fetchTrackData]);

  // Обработчик изменений в localStorage (с защитой от повторов)
  const handleStorageChange = useCallback((event) => {
    try {
      // Проверяем, не слишком ли недавно мы сами обновили данные
      const now = new Date().getTime();
      const lastUpdate = window.lastVehicleUpdateTime || 0;
      
      // Если обновление было менее 2 секунд назад и это мы сами его сделали, игнорируем
      if ((now - lastUpdate) < 2000) {
        console.log('TrackReport: Игнорируем событие storage - недавнее собственное обновление');
        return;
      }
      
      if (event.key === 'lastDateRange') {
        console.log('TrackReport: Обнаружено изменение диапазона дат в localStorage');
        try {
          const newRange = JSON.parse(event.newValue);
          if (newRange && newRange.startDate && newRange.endDate) {
            const updatedRange = {
              startDate: ensureValidDate(newRange.startDate),
              endDate: ensureValidDate(newRange.endDate)
            };
            
            if (updatedRange.startDate && updatedRange.endDate) {
              setCurrentDateRange(updatedRange);
              
              // Если транспортное средство уже выбрано, обновляем данные
              if (currentVehicle && currentVehicle.id && mapInitialized) {
                safeFetchTrackData(currentVehicle, updatedRange, 'localStorage_dateChange');
              }
            }
          }
        } catch (error) {
          console.warn('TrackReport: Ошибка при разборе диапазона дат из localStorage:', error);
        }
      } else if (event.key === 'lastSelectedVehicle') {
        console.log('TrackReport: Обнаружено изменение выбранного транспорта в localStorage');
        try {
          const newVehicle = JSON.parse(event.newValue);
          if (newVehicle && newVehicle.id) {
            console.log('TrackReport: Новые данные транспорта:', newVehicle);
            
            // Проверяем, отличается ли новый транспорт от текущего
            const vehicleChanged = !currentVehicle || currentVehicle.id !== newVehicle.id;
            
            if (vehicleChanged) {
              console.log('TrackReport: Транспорт изменился, обновляем состояние компонента');
              
              // Добавляем информацию об источнике данных
              const updatedVehicle = {
                ...newVehicle,
                _updateTimestamp: new Date().getTime(),
                _source: 'localStorage'
              };
              
              setCurrentVehicle(updatedVehicle);
              
              // Если диапазон дат уже выбран, обновляем данные
              if (currentDateRange.startDate && currentDateRange.endDate && mapInitialized) {
                safeFetchTrackData(updatedVehicle, currentDateRange, 'localStorage_vehicleChange');
              }
            } else {
              console.log('TrackReport: Транспорт не изменился, обновление не требуется');
            }
          }
        } catch (error) {
          console.warn('TrackReport: Ошибка при разборе данных транспорта из localStorage:', error);
        }
      }
    } catch (error) {
      console.error('TrackReport: Ошибка при обработке изменений в localStorage:', error);
    }
  }, [currentVehicle, currentDateRange, mapInitialized, ensureValidDate, setCurrentVehicle, setCurrentDateRange, safeFetchTrackData]);

  // Инициализация при монтировании компонента (точная копия из TrackMap)
  useEffect(() => {
    if (!isInitialized.current) {
      console.log('TrackReport: Инициализация компонента с автоматической загрузкой');
      
      // Проверяем localStorage при загрузке компонента
      checkLocalStorage();
      
      // Инициализация карты с задержкой для предотвращения ошибок DOM
      if (mapContainerRef.current && !mapInitialized) {
        setTimeout(() => {
          if (mapContainerRef.current && !mapInitialized) {
            console.log('TrackReport: Запуск инициализации карты из useAutoLoad');
            initializeMap();
          } else {
            console.log('TrackReport: Пропуск инициализации карты - уже инициализирована или контейнер недоступен');
          }
        }, 100);
      }
    }
    
    // Слушатель события изменения данных в localStorage
    window.addEventListener('storage', handleStorageChange);
    
    // Очистка обработчиков при размонтировании
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      
      // Очищаем таймауты
      if (fetchTimeout.current) {
        clearTimeout(fetchTimeout.current);
        fetchTimeout.current = null;
      }
    };
  }, [checkLocalStorage, mapContainerRef, mapInitialized, initializeMap, handleStorageChange]);

  // Эффект при изменении props (с защитой от повторов)
  useEffect(() => {
    // Обновляем состояние и вызываем обновление данных при изменении vehicle
    if (vehicle && (!currentVehicle || vehicle.id !== currentVehicle?.id)) {
      console.log('TrackReport: Получен новый props vehicle:', vehicle);
      
      // Сбрасываем флаг автозагрузки при смене транспорта
      autoLoadCompleted.current = false;
      
      // Добавляем метку времени для принудительного обновления
      const vehicleWithTimestamp = {
        ...vehicle,
        _updateTimestamp: new Date().getTime(),
        _source: 'props' // Добавляем источник данных
      };
      
      // Обновляем текущий транспорт
      setCurrentVehicle(vehicleWithTimestamp);
      
      // Сохраняем в localStorage (важно для синхронизации с другими компонентами)
      saveVehicleToLocalStorage(vehicle);
      
      // Принудительно обновляем трек, если инициализирована карта
      if (mapInitialized && currentDateRange.startDate && currentDateRange.endDate) {
        safeFetchTrackData(vehicleWithTimestamp, currentDateRange, 'props_vehicle');
      } else if (!mapInitialized && mapContainerRef.current) {
        // Если карта еще не инициализирована, инициализируем её сразу
        console.log('TrackReport: Инициализация карты при первой загрузке с новым транспортом');
        setTimeout(() => initializeMap(), 100);
      }
    }
    
    // Обновляем даты, если они изменились в props
    if (startDate && endDate && 
        (startDate !== currentDateRange.startDate || 
         endDate !== currentDateRange.endDate)) {
      
      // Сбрасываем флаг автозагрузки при смене дат
      autoLoadCompleted.current = false;
      
      // Создаем новый объект с диапазоном дат
      const newDateRange = { 
        startDate: ensureValidDate(startDate),
        endDate: ensureValidDate(endDate)
      };
      
      if (newDateRange.startDate && newDateRange.endDate) {
        console.log('TrackReport: Обновление диапазона дат из props:', {
          startDate: newDateRange.startDate.toISOString(),
          endDate: newDateRange.endDate.toISOString()
        });
        
        setCurrentDateRange(newDateRange);
        saveDateRangeToLocalStorage(newDateRange);
        
        // Если уже есть выбранное транспортное средство, обновляем данные
        if (currentVehicle && currentVehicle.id && mapInitialized) {
          safeFetchTrackData(currentVehicle, newDateRange, 'props_dateRange');
        }
      }
    }
  }, [vehicle, startDate, endDate, currentVehicle, currentDateRange, mapInitialized, mapContainerRef, 
      setCurrentVehicle, saveVehicleToLocalStorage, setCurrentDateRange, saveDateRangeToLocalStorage, 
      initializeMap, ensureValidDate, formatDate, safeFetchTrackData]);

  // Эффект для автоматической загрузки трека при инициализации карты
  useEffect(() => {
    if (mapInitialized && currentVehicle && currentVehicle.id && 
        currentDateRange.startDate && currentDateRange.endDate && 
        !autoLoadCompleted.current) {
      console.log('TrackReport: Карта инициализирована, автоматически загружаем трек:', {
        vehicle: currentVehicle.name,
        startDate: currentDateRange.startDate.toISOString(),
        endDate: currentDateRange.endDate.toISOString()
      });
      
      // Помечаем что автозагрузка выполнена
      autoLoadCompleted.current = true;
      
      // Запускаем загрузку с небольшой задержкой
      setTimeout(() => {
        safeFetchTrackData(currentVehicle, currentDateRange, 'map_initialized');
      }, 500);
    }
  }, [mapInitialized, currentVehicle, currentDateRange, safeFetchTrackData]);

  // Очистка при размонтировании
  useEffect(() => {
    return () => {
      // Сбрасываем флаги
      isInitialized.current = false;
      lastVehicleId.current = null;
      lastDateRange.current = null;
      autoLoadCompleted.current = false;
      
      // Очищаем таймауты
      if (fetchTimeout.current) {
        clearTimeout(fetchTimeout.current);
        fetchTimeout.current = null;
      }
    };
  }, []);

  return {
    checkLocalStorage,
    handleStorageChange,
    safeFetchTrackData
  };
}; 